import { useState, useEffect } from "react";

interface PWAState {
  isInstalled: boolean;
  isInstallable: boolean;
  deferredPrompt: any;
  isStandalone: boolean;
  isIOS: boolean;
  isAndroid: boolean;
}

interface PWAActions {
  installPWA: () => Promise<boolean>;
  dismissPrompt: () => void;
  canInstallAutomatically: () => Promise<boolean>;
}

export const usePWA = (): PWAState & PWAActions => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);

  // Detect platform
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches;

  // Check PWA criteria
  const checkPWACriteria = async () => {
    const criteria = {
      serviceWorker: "serviceWorker" in navigator,
      manifest: false,
      https:
        window.location.protocol === "https:" ||
        window.location.hostname === "localhost",
      standalone: window.matchMedia("(display-mode: standalone)").matches,
    };

    // Check if manifest is available
    try {
      const response = await fetch("/manifest.json");
      criteria.manifest = response.ok;
    } catch (error) {
      // Silently handle manifest check error
    }

    return criteria;
  };

  // runs only once
  useEffect(() => {
    const isStandalone = window.matchMedia(
      "(display-mode: standalone)",
    ).matches;
    const isInstalled = localStorage.getItem("pwa-installed") === "yes";
    const isIOSStandalone = (window.navigator as any).standalone === true;

    setIsInstalled(isStandalone || isInstalled || isIOSStandalone);

    // Listener for when the app is installed
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      localStorage.setItem("pwa-installed", "yes");
    };

    window.addEventListener("appinstalled", handleAppInstalled);
    return () => window.removeEventListener("appinstalled", handleAppInstalled);
  }, []);

  // Capture beforeinstallprompt event using global prompt
  useEffect(() => {
    const checkGlobalPrompt = () => {
      const globalPrompt = (window as any).realInstallPrompt;
      if (globalPrompt) {
        setDeferredPrompt(globalPrompt);
        setIsInstallable(true);
        return true;
      }
      return false;
    };

    if (checkGlobalPrompt()) {
      return;
    }

    // Listener for custom capture event
    const handlePromptCaptured = (e: any) => {
      setDeferredPrompt(e.detail);
      setIsInstallable(true);
    };

    window.addEventListener("pwa-prompt-captured", handlePromptCaptured);

    const checkInterval = setInterval(() => {
      if (!deferredPrompt && checkGlobalPrompt()) {
        clearInterval(checkInterval);
      }
    }, 1000);

    const finalCheck = setTimeout(() => {
      clearInterval(checkInterval);
    }, 15000);

    return () => {
      window.removeEventListener("pwa-prompt-captured", handlePromptCaptured);
      clearInterval(checkInterval);
      clearTimeout(finalCheck);
    };
  }, [deferredPrompt]);

  const canInstallAutomatically = async (): Promise<boolean> => {
    const criteria = await checkPWACriteria();
    return criteria.serviceWorker && criteria.manifest && criteria.https;
  };

  const installPWA = async (): Promise<boolean> => {
    try {
      if (window.matchMedia("(display-mode: standalone)").matches) {
        setIsInstalled(true);
        localStorage.setItem("pwa-installed", "yes");
        return true;
      }

      // Use the globally captured prompt FIRST
      const realPrompt = (window as any).realInstallPrompt || deferredPrompt;

      if (realPrompt && typeof realPrompt.prompt === "function") {
        try {
          await realPrompt.prompt();

          const userChoice = await realPrompt.userChoice;

          if (userChoice.outcome === "accepted") {
            setIsInstalled(true);
            setIsInstallable(false);
            localStorage.setItem("pwa-installed", "yes");

            setDeferredPrompt(null);
            (window as any).realInstallPrompt = null;

            return true;
          } else {
            return false;
          }
        } catch (promptError) {
          return false;
        }
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  };

  const dismissPrompt = () => {
    setDeferredPrompt(null);
    setIsInstallable(false);
  };

  return {
    isInstalled,
    isInstallable,
    deferredPrompt,
    isStandalone,
    isIOS,
    isAndroid,
    installPWA,
    dismissPrompt,
    canInstallAutomatically,
  };
};
